export const BLOG_PREFIX = "/insights";
export const SITE_ORIGIN = "https://launchguide.io";

export type BlogPostStatus = "draft" | "published";

export type BlogPost = {
  id: string;
  external_id: string;
  slug: string;
  title: string;
  content_html: string;
  content_markdown: string | null;
  excerpt: string;
  meta_title: string | null;
  meta_description: string | null;
  tags: string[] | null;
  keywords: string[] | null;
  json_ld: Record<string, unknown> | null;
  status: BlogPostStatus;
  created_at: string | null;
  published_at: string | null;
  updated_at: string;
  webhook_timestamp: string | null;
};

export function blogPostPath(slug: string): string {
  return `${BLOG_PREFIX}/${encodeURIComponent(slug)}`;
}

export function blogPostUrl(slug: string): string {
  return `${SITE_ORIGIN}${blogPostPath(slug)}`;
}

export function blogIndexPath(): string {
  return BLOG_PREFIX;
}

export function blogIndexUrl(): string {
  return `${SITE_ORIGIN}${BLOG_PREFIX}`;
}

export function resolveDocumentTitle(post: Pick<BlogPost, "meta_title" | "title">): string {
  const base = post.meta_title?.trim() || post.title.trim();
  return base.includes("LaunchGuide") ? base : `${base} — LaunchGuide.io`;
}

export function resolveMetaDescription(
  post: Pick<BlogPost, "meta_description" | "excerpt" | "content_html">
): string {
  if (post.meta_description?.trim()) return post.meta_description.trim();
  if (post.excerpt?.trim()) return post.excerpt.trim();
  return stripHtml(post.content_html).slice(0, 160).trim();
}

export function formatBlogDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function stripHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizeSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function deriveSlugFromTitle(title: string): string {
  return normalizeSlug(title) || "post";
}

export function deriveSlugFromId(id: string): string {
  return normalizeSlug(id.split("-")[0]) || "post";
}
